import { Input, Modal } from 'antd';
import { AdminAccess, useMarkPendingDeceasedAdminAccessMutation } from 'api/AdminAccessApi';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { logging } from 'utils/utils';
import './ConfirmModal.css';

export const CONFIRM_DECEASED_MODAL_NAME = 'CONFIRM_DECEASED_MODAL_NAME';

export type ConfirmModalProps = {
  adminAccessId: string;
  adminAccess?: AdminAccess | null;
};

const getPatientDisplayName = (adminAccess?: AdminAccess | null) =>
  adminAccess?.beneficiaryDisplayName || adminAccess?.displayName || 'this patient';

const getPatientEmail = (adminAccess?: AdminAccess | null) =>
  adminAccess?.beneficiaryEmail || adminAccess?.email || null;

const normalizeConfirmationValue = (value?: string | null) => value?.trim().toLowerCase() || '';

function ConfirmModal({ adminAccessId, adminAccess }: ConfirmModalProps) {
  const dispatch = useAppDispatch();

  const [confirmationValue, setConfirmationValue] = useState('');

  const [markPendingDeceasedAdminAccess] = useMarkPendingDeceasedAdminAccessMutation();

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const moveToUnverifiedDeceased = async () => {
    if (!isValid) {
      return;
    }

    try {
      await markPendingDeceasedAdminAccess({ id: adminAccessId }).unwrap();

      handleCloseModal();
    } catch (e: any) {
      logging(e);
    }
  };

  const expectedEmail = getPatientEmail(adminAccess);
  const patientDisplayName = getPatientDisplayName(adminAccess);
  const expectedName = adminAccess?.beneficiaryDisplayName || adminAccess?.displayName || null;
  const normalizedInput = normalizeConfirmationValue(confirmationValue);
  const normalizedExpectedEmail = normalizeConfirmationValue(expectedEmail);
  const normalizedExpectedName = normalizeConfirmationValue(expectedName);
  const canValidate = Boolean(normalizedExpectedEmail || normalizedExpectedName);
  const isValid = canValidate
    ? normalizedInput !== '' && (normalizedInput === normalizedExpectedEmail || normalizedInput === normalizedExpectedName)
    : true;
  const isConfirmDisabled = canValidate && !isValid;

  return (
    <Modal
      open
      title={`Confirm ${patientDisplayName}'s Passing`}
      onCancel={handleCloseModal}
      okText="Confirm"
      onOk={moveToUnverifiedDeceased}
      okButtonProps={{ disabled: isConfirmDisabled }}
    >
      <>
        <p>We understand this may be a difficult step.</p>
        <p>
          By continuing, you are confirming that {patientDisplayName} has passed away. {patientDisplayName}&apos;s account
          will enter a seven-day review period before the status is finalized and any scheduled messages begin to be
          delivered.
        </p>
        <p>During this time, the update can be corrected if it was made by mistake.</p>
        <p>
          To help ensure you are updating the correct account, please enter {patientDisplayName}&apos;s full name or email
          address below.
        </p>
        {canValidate ? (
          <>
            <p>Patient&apos;s full name or email</p>
            <p>
              <b>{[expectedName, expectedEmail].filter(Boolean).join(' or ')}</b>
            </p>
            <Input
              placeholder="Patient's full name or email"
              onChange={(e) => setConfirmationValue(e.target.value)}
              status={isValid ? '' : 'error'}
            />
            {!isValid && <p className="alert-text">Please enter the patient&apos;s email or full name</p>}
          </>
        ) : (
          <p>No email or name is available for this patient. Click Confirm to continue.</p>
        )}
      </>
    </Modal>
  );
}

function AddAdminModalComponentWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);

  const adminAccess = modalData?.adminAccess;
  const adminAccessId = modalData?.adminAccessId;

  return modalName === CONFIRM_DECEASED_MODAL_NAME && adminAccessId ? (
    <ConfirmModal adminAccessId={adminAccessId} adminAccess={adminAccess} />
  ) : null;
}

export default AddAdminModalComponentWrapper;
